import 'virtual:svg-icons-register';
import './style/main.scss';

// Vendor components
import { createPinia } from 'pinia';
import UAParser from 'ua-parser-js';
import { createApp } from 'vue';
import VueClipboard from 'vue-clipboard2';
import VueDOMPurifyHTML from 'vue-dompurify-html';

import App from './App.vue';
import { directiveClickOutside, directiveFocus } from './directives';
import * as log from './logger';
import router from './router';
import * as settings from './utils/settings';

if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_DEVSERVER_PROXY_URL) {
  async function initMSW() {
    const { server } = await import('../mocks/server');
    await server.start({
      quiet: true,
      onUnhandledRequest: 'bypass',
      serviceWorker: {
        url: '/mockServiceWorker.js',
      },
    });
  }
  // defining this function IN the conditional keeps it out of prod bundle
  await initMSW();

  // Dev-only Mock Scenario overlay. Opt out with VITE_DISABLE_MOCK_PANEL=true
  // (mirrors VITE_DISABLE_VUE_DEVTOOLS); MSW itself still runs.
  if (import.meta.env.VITE_DISABLE_MOCK_PANEL !== 'true') {
    const { mountMockPanel } = await import('../mocks/mountMockPanel');
    mountMockPanel();
  }
}

const app = createApp(App);

// Register Directives
app.directive('click-outside', directiveClickOutside);
app.directive('focus', directiveFocus);

/**
 * Measure and log initial page load time
 * See https://developer.mozilla.org/en-US/docs/Web/API/PerformanceTiming
 */
// TODO revise this PerformanceTiming is deprecated
window.onload = () => {
  try {
    const loadTime =
      window.performance.timing.domContentLoadedEventEnd -
      window.performance.timing.navigationStart;
    log.info(`Page load time is ${loadTime} ms`);
  } catch (err) {
    log.warn('Error while calculating page load performance', err);
  }
};

app.use(VueClipboard);
app.use(VueDOMPurifyHTML);

/**
 * Configure global error/warning handlers
 * @param err
 * @param vm
 * @param info - a Vue-specific error info, e.g. which lifecycle hook the error was found in.
 */
app.config.errorHandler = (err, vm, info) => {
  // eslint-disable-next-line no-console
  console.error(err, vm, info);
  log.send('error', 'Unhandled global error (err, info)', err, info);
};
app.config.warnHandler = (err, vm, info) => {
  // eslint-disable-next-line no-console
  console.log(err, vm, info);
  log.send('warn', 'Unhandled global warning (err, info)', err, info);
};

app.use(createPinia());
app.use(router);

/**
 * Load global settings from local storage to Vue state
 */
settings.loadSettingsFromLocalStorage();

const browserData = new UAParser().getResult();
browserData.dimensions = `${window.innerWidth}w x ${window.innerHeight}h`;
log.info('Browser data', browserData);

app.mount('#app');
